import { useMemo } from "react";
import { isEqual } from "lodash";

import { selectLayerConfigByKey, selectVersionConfig } from "fond/api";
import { MapboxStyleLayer } from "fond/types";
import { LayerConfig, SublayerConfig } from "fond/types/ProjectLayerConfig";
import { useAppSelector } from "fond/utils/hooks";

import { addFilterCondition, transformExpressionsForDrawMode, transformFilterForDrawMode, transformLayerConfigStylesForDrawing } from "../helper";

import { validationStyles } from "./styles";

/**
 * Type guard to check if a config entity is a LayerConfig with Styles property
 */
const isLayerConfig = (config: unknown): config is LayerConfig => {
  return !!config && typeof config === "object" && "Styles" in config && Array.isArray((config as LayerConfig).Styles);
};

/**
 * Type guard to check if a config entity has Styles (LayerConfig or SublayerConfig)
 */
const hasStyles = (config: unknown): config is { Styles: string[] } => {
  return !!config && typeof config === "object" && "Styles" in config && Array.isArray((config as { Styles: string[] }).Styles);
};

/**
 * Creates validation styles for a specific layer based on its geometry type.
 * Only includes validation styles that match the layer's geometry type.
 *
 * @param layerId - The layer ID to create validation styles for
 * @param geometryType - The geometry type of the layer (Point, LineString, or Polygon)
 * @returns Array of validation styles specific to this layer
 */
const createLayerValidationStyles = (layerId: string, geometryType: LayerConfig["GeometryType"]): MapboxStyleLayer[] => {
  const layerValidationStyles: MapboxStyleLayer[] = [];

  // Return early if geometry type is null or "Null" - no validation styles needed
  if (!geometryType || geometryType === "Null") {
    return layerValidationStyles;
  }

  // Map geometry types to their validation style prefixes
  // Multi-geometry types (MultiPoint, MultiLineString, MultiPolygon) use the same styles as their base types
  const geometryTypeToStylePrefix: Record<string, string[]> = {
    Point: ["gl-draw-point-invalid", "gl-draw-point-stroke-invalid"],
    MultiPoint: ["gl-draw-point-invalid", "gl-draw-point-stroke-invalid"],
    LineString: ["gl-draw-line-invalid"],
    MultiLineString: ["gl-draw-line-invalid"],
    Polygon: ["gl-draw-polygon-fill-invalid", "gl-draw-polygon-stroke-invalid"],
    MultiPolygon: ["gl-draw-polygon-fill-invalid", "gl-draw-polygon-stroke-invalid"],
  };

  const relevantStylePrefixes = geometryTypeToStylePrefix[geometryType];
  if (!relevantStylePrefixes) return layerValidationStyles;

  // Filter validation styles to only those matching this geometry type
  const relevantValidationStyles = validationStyles.filter((style) => relevantStylePrefixes.some((prefix) => style.id.startsWith(prefix)));

  // Create layer-specific copies of these validation styles
  relevantValidationStyles.forEach((style) => {
    // Remove $type from filter since we're creating geometry-specific styles
    // Convert legacy filter conditions to expression syntax for compatibility
    // with sublayer filters that use expression syntax (cannot mix legacy and expression syntax)
    let filterConditions: unknown[] = [];
    if (style.filter && Array.isArray(style.filter) && style.filter[0] === "all") {
      filterConditions = style.filter
        .slice(1)
        .filter((condition) => {
          if (!Array.isArray(condition)) return true;
          return condition[0] !== "==" || condition[1] !== "$type";
        })
        .map((condition) => {
          // Convert legacy 3-element conditions ["op", "prop", "value"] to expression ["op", ["get", "prop"], "value"]
          if (Array.isArray(condition) && condition.length === 3 && typeof condition[1] === "string" && !Array.isArray(condition[1])) {
            return [condition[0], ["get", condition[1]], condition[2]];
          }
          return condition;
        });
    }

    // Add layerId check to the filter (using expression syntax)
    filterConditions.push(["==", ["get", "user_layerId"], layerId]);

    layerValidationStyles.push({
      ...style,
      id: `${layerId}-${style.id}`,
      filter: ["all", ...filterConditions],
    });
  });

  return layerValidationStyles;
};

/**
 * Helper to apply active state styling to a Mapbox layer style.
 * When a feature has `active: true`, it will be highlighted in the specified color.
 *
 * @param style - The MapboxStyle to apply active styling to
 * @param activeColor - The color to use for active features (default: "yellow")
 * @returns The paint object with active styling applied
 */
const applyActiveStateStyling = (style: Pick<MapboxStyleLayer, "type" | "paint">, activeColor: string = "yellow"): MapboxStyleLayer["paint"] => {
  const { type, paint } = style;

  if (!paint) return paint;

  switch (type) {
    case "symbol": {
      const symbolPaint = paint as Record<string, unknown>;
      return {
        ...paint,
        "text-color": ["case", ["==", ["get", "active"], "true"], activeColor, symbolPaint["text-color"] || "black"],
        "text-halo-color": ["case", ["==", ["get", "active"], "true"], activeColor, symbolPaint["text-halo-color"] || "black"],
      };
    }
    case "line": {
      const linePaint = paint as Record<string, unknown>;
      return {
        ...paint,
        "line-color": ["case", ["==", ["get", "active"], "true"], activeColor, linePaint["line-color"] || "black"],
      };
    }
    case "circle": {
      const circlePaint = paint as Record<string, unknown>;
      return {
        ...paint,
        "circle-color": ["case", ["==", ["get", "active"], "true"], activeColor, circlePaint["circle-color"] || "black"],
      };
    }
    default:
      return paint;
  }
};

/**
 * Custom hook to get drawing styles for multiple layers from the layer configuration.
 *
 * This hook processes all layer keys at once to comply with React's Rules of Hooks.
 * For layers with ShowSublayers enabled, it generates separate styles for each sublayer
 * to support independent visibility toggling.
 *
 * @param versionId - The version ID to get the layers from
 * @param layerKeys - Array of layer keys (e.g., ["input_layers/address", "input_layers/street"])
 * @param sublayerLayerKeys - Set of layer keys that have ShowSublayers enabled. When enabled,
 *   validation styles are delegated to individual sublayers instead of the parent layer.
 * @returns Map of layerKey (or sublayerId) to MapboxStyleLayer array, or undefined if not available
 */
export const useMultiLayerDrawingStyles = (
  versionId: string,
  layerKeys: string[],
  sublayerLayerKeys: Set<string> = new Set()
): Map<string, MapboxStyleLayer[] | undefined> => {
  const config = useAppSelector((state) => selectVersionConfig(state, versionId));

  // Get all layer configs in one selector call
  // Use LayerConfig directly since it's always available (even when Layer doesn't exist yet)
  const layerConfigs = useAppSelector((state) => {
    return layerKeys.map((layerKey) => {
      const layerConfig = selectLayerConfigByKey(state, { versionId, layerKey });
      return {
        layerKey,
        layerConfig,
      };
    });
  }, isEqual);

  return useMemo(() => {
    const stylesMap = new Map<string, MapboxStyleLayer[] | undefined>();

    if (!config?.Data?.entities) {
      // Return empty map if we don't have the required data
      layerKeys.forEach((key) => stylesMap.set(key, undefined));
      return stylesMap;
    }

    // Process each layer
    layerConfigs.forEach(({ layerKey, layerConfig }) => {
      // If we don't have any layer config, skip this layer
      if (!layerConfig || typeof layerConfig !== "object") {
        stylesMap.set(layerKey, undefined);
        return;
      }

      // When ShowSublayers is enabled, validation styles are handled per-sublayer
      // so the parent layer should not include them
      const hasShowSublayers = sublayerLayerKeys.has(layerKey);

      // Process parent layer styles if it has them
      if (isLayerConfig(layerConfig)) {
        // Get base styles for the parent layer (no sublayer filters)
        const baseLayerStyles = layerConfig.Styles.map((styleId) => {
          const style = config.Data.entities[styleId];
          return style && "MapboxStyle" in style ? style : null;
        }).filter((style): style is NonNullable<typeof style> => style !== null);

        // Convert to Mapbox styles without sublayer filters
        const parentMapboxStyles = baseLayerStyles
          .map((layerStyle) => {
            if (!layerStyle.MapboxStyle) return null;

            const paint = applyActiveStateStyling(layerStyle.MapboxStyle);

            return {
              ...layerStyle.MapboxStyle,
              id: layerStyle.ID,
              paint,
            } as MapboxStyleLayer;
          })
          .filter((style): style is MapboxStyleLayer => style !== null);

        if (parentMapboxStyles.length > 0) {
          // Transform for use with drawing tools
          const transformed = transformLayerConfigStylesForDrawing(layerKey, parentMapboxStyles);

          // Only add validation styles to parent when sublayers are not shown independently
          const geometryType = layerConfig.GeometryType;
          const validationStylesForLayer = !hasShowSublayers && geometryType ? createLayerValidationStyles(layerKey, geometryType) : [];

          // Combine and add to map
          const combinedStyles = [...validationStylesForLayer, ...transformed];
          stylesMap.set(layerKey, combinedStyles);
        } else if (!hasShowSublayers) {
          // Parent has no regular styles, but might have validation styles
          const geometryType = layerConfig.GeometryType;
          const validationStylesForLayer = geometryType ? createLayerValidationStyles(layerKey, geometryType) : [];

          if (validationStylesForLayer.length > 0) {
            stylesMap.set(layerKey, validationStylesForLayer);
          } else {
            stylesMap.set(layerKey, undefined);
          }
        } else {
          stylesMap.set(layerKey, undefined);
        }
      } else if (!hasShowSublayers) {
        // Parent layer has no styles, but we still want to create validation styles if it has a geometry
        const geometryType = (layerConfig as LayerConfig).GeometryType;
        const validationStylesForLayer = geometryType ? createLayerValidationStyles(layerKey, geometryType) : [];

        if (validationStylesForLayer.length > 0) {
          stylesMap.set(layerKey, validationStylesForLayer);
        } else {
          stylesMap.set(layerKey, undefined);
        }
      } else {
        stylesMap.set(layerKey, undefined);
      }

      // Process sublayers separately (if any)
      // This should happen regardless of whether parent has styles
      if ("Children" in layerConfig && layerConfig.Children) {
        layerConfig.Children.forEach((childId) => {
          const childConfig = config.Data.entities[childId];
          if (!hasStyles(childConfig)) return;

          const isSublayer = "Type" in childConfig && childConfig.Type === "SUBLAYER";
          if (!isSublayer) return;

          const sublayerConfig = childConfig as SublayerConfig;
          const sublayerFilter = sublayerConfig.FilterConfiguration?.Mapbox;
          if (!sublayerFilter) return;

          const transformedFilter = transformFilterForDrawMode(sublayerFilter);

          // Get sublayer styles
          const sublayerStyles = sublayerConfig.Styles.map((styleId) => {
            const style = config.Data.entities[styleId];
            return style && "MapboxStyle" in style ? style : null;
          }).filter((style): style is NonNullable<typeof style> => style !== null);

          // Convert to Mapbox styles with sublayer filter
          const sublayerMapboxStyles = sublayerStyles
            .map((layerStyle) => {
              if (!layerStyle.MapboxStyle) return null;

              const combinedFilter = addFilterCondition(layerStyle.MapboxStyle.filter, transformedFilter);
              const paint = applyActiveStateStyling(layerStyle.MapboxStyle);

              return {
                ...layerStyle.MapboxStyle,
                id: layerStyle.ID,
                filter: combinedFilter,
                // Transform paint/layout expressions to use user_ prefix (Mapbox Draw prefixes all feature properties)
                ...(paint && { paint: transformExpressionsForDrawMode(paint) }),
                ...(layerStyle.MapboxStyle.layout && { layout: transformExpressionsForDrawMode(layerStyle.MapboxStyle.layout) }),
              } as MapboxStyleLayer;
            })
            .filter((style): style is MapboxStyleLayer => style !== null);

          // Create validation styles for sublayer:
          // - use parent layerKey so user_layerId filter matches actual features
          // - prefix style IDs with childId for independent visibility control
          const geometryType = layerConfig.GeometryType;
          const validationStylesForSublayer = geometryType ? createLayerValidationStyles(layerKey, geometryType) : [];

          // Update validation style filters to include the sublayer-specific filter
          // and prefix style IDs with the sublayer ID
          const filteredValidationStyles = validationStylesForSublayer.map((style) => ({
            ...style,
            id: `${childId}-${style.id}`,
            filter: addFilterCondition(style.filter, transformedFilter),
          }));

          if (sublayerMapboxStyles.length > 0) {
            // Transform for use with drawing tools
            // Important: Use parent layer key for user_layerId filter since features belong to parent layer
            // but use sublayer ID as prefix for style IDs to enable independent visibility control
            const transformedWithParentFilter = sublayerMapboxStyles.map((style) => ({
              ...style,
              id: `${childId}-${style.id}`,
              filter: addFilterCondition(style.filter, ["==", ["get", "user_layerId"], layerKey]),
            }));

            // Combine regular and validation styles
            const combinedStyles = [...filteredValidationStyles, ...transformedWithParentFilter];
            stylesMap.set(childId, combinedStyles);
          } else if (filteredValidationStyles.length > 0) {
            // Sublayer has no regular styles but still needs validation styles
            stylesMap.set(childId, filteredValidationStyles);
          }
        });
      }
    });

    return stylesMap;
  }, [layerConfigs, layerKeys, config, sublayerLayerKeys]);
};
